import { useMemo } from 'react';
import { countries } from 'countries-list';

/**
 * Country picker — native `<select>` styled to match the editorial
 * slate+emerald system, populated from `countries-list` (252 entries:
 * ISO-2 → name + native name + continent).
 *
 * Flag emojis are derived from the ISO-2 code via Unicode regional
 * indicator symbols (no extra package needed) — modern browsers + OSes
 * render these natively as flags. Falls back gracefully to the two-letter
 * code on systems without emoji support.
 *
 * Why a native <select> over a custom dropdown:
 *   - 252 options is fine for native select (first-letter typeahead works)
 *   - Keyboard + screen-reader accessibility for free (native widget)
 *   - Matches our existing design (Settings, Entities, FX, Workforce all
 *     use native selects with the same border-ink-200 / rounded-md styling)
 *   - Zero extra JS — the bundle adds ~30 KB raw for the data and that's it
 *
 * Style props let callers swap classes to match their context (rounded-lg
 * on auth/onboarding, rounded-md elsewhere).
 */
export function CountryPicker({
  value,
  onChange,
  className,
  id,
  ariaLabel,
  disabled,
}: {
  value: string;                       // ISO-2 code, uppercase
  onChange: (iso2: string) => void;
  className?: string;
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  const options = useMemo(() => buildOptions(), []);

  return (
    <select
      id={id}
      value={value.toUpperCase()}
      onChange={(e) => onChange(e.target.value.toUpperCase())}
      disabled={disabled}
      aria-label={ariaLabel}
      className={
        className ??
        'w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm focus:border-accent-500 focus:ring-2 focus:ring-accent-500/30 outline-none'
      }
    >
      {options.map((o) => (
        <option key={o.code} value={o.code} title={o.title}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Convert an ISO 3166-1 alpha-2 country code (e.g. "JP", "us") into the
 * corresponding flag emoji (🇯🇵, 🇺🇸).
 *
 * Unicode encodes flag emojis as a pair of Regional Indicator Symbol
 * letters starting at U+1F1E6 (REGIONAL INDICATOR SYMBOL LETTER A).
 * So 'A' (U+0041) maps to 🇦 (U+1F1E6), 'B' to 🇧, etc. Concatenating
 * two of these produces the flag for the corresponding 2-letter code.
 *
 * The offset 127397 = 0x1F1E6 − 0x41 = 0x1F1A5.
 */
export function flagEmojiFor(iso2: string): string {
  const code = iso2.trim().toUpperCase();
  if (code.length !== 2 || !/^[A-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(
    127397 + code.charCodeAt(0),
    127397 + code.charCodeAt(1),
  );
}

interface Option {
  code: string;
  /** Visible label: `🇯🇵 Japan` */
  label: string;
  /** Hover tooltip: `Japan (日本)` */
  title: string;
}

function buildOptions(): Option[] {
  return Object.entries(countries)
    .map(([code, c]) => {
      const emoji = flagEmojiFor(code);
      const native = c.native && c.native !== c.name ? ` (${c.native})` : '';
      return {
        code,
        label: `${emoji} ${c.name}`,
        title: `${c.name}${native}`,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}
